import { engineComponent, ArrayUtils, NumberUtils } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../Components/ComponentTypes";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../Utils/FrameTimer";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { TimelineElement } from "../Components/Elements/Graphics/TimelineElement";
import { Configuration } from "../Components/Configuration";
import { AppContext } from "../../Common/AppContext";
import { BetManager } from "../Managers/BetManager";
import { ImageTextCounterElement } from "../Components/Elements/Texts/ImageTextCounterElement";
import { GridElement } from "./GridElement";
import { BetElement } from "./BetElement";
import { RouletteBetType } from "../CustomComponents/RouletteBetType";
import { SwitchStyleCaseAttributes } from "../Components/Elements/Styles/SwitchStyle";
import { AnimationElement } from "../Components/Elements/Graphics/AnimationElement";
import { BaseElement } from "../Components/Elements/Graphics/BaseElement";
import { Dolly } from "./Dolly";
import { PlayList } from "../Components/Sounds/PlayList";
import { LayoutContainerElement } from "../CustomComponents/LayoutContainerElement";
import { StageController } from "../Components/StageController";
import { DataManager } from "../Managers/DataManager";

/**
 * WinAnimator uses paylines and symbols to create presentation of wins to user.
 * Also handle high wins animation for presentation special screens to user when win is bigger than some defined values.
 * @class WinAnimator
 * @extends ContainerElement
 */
@engineComponent(ComponentTypes.DisplayObject)
export class WinAnimator extends LayoutContainerElement {
    //******************************************************************************************************************
    //  EVENTS
    //******************************************************************************************************************

    public static readonly FinishWinCalculation: string = "eventFinishWinCalculation";

    public static readonly FinishWinAnimator: string = "eventFinishWinAnimator";

    public static readonly FinishWinNumberAnimator: string = "eventFinishWinNumberAnimator";

    //******************************************************************************************************************
    //  STATUSES
    //******************************************************************************************************************

    public static readonly StatusShowWinNumberFinished: string = "statusShowWinNumberFinished";

    public static readonly StatusShowWinCombinationDelay: string = "statusShowWinCombinationDelay";

    public static readonly StatusShowWinCombinationFinished: string = "statusShowWinCombinationFinished";

    public static readonly StatusShowWinAnimationFinished: string = "statusShowWinAnimationFinished";

    public static readonly StatusShowWinsFinished: string = "statusShowWinFinished";

    //******************************************************************************************************************
    //  SETTINGS
    //******************************************************************************************************************

    private static readonly ShowWinCombinationDelayDuration: number = 15;

    private static readonly ShowWinCombinationDuration: number = 60;

    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    protected gridElements: { [id: string]: GridElement };
    protected betElements: { [id: string]: BetElement };
    protected gridWinPoint: BaseElement;
    protected dolly: Dolly;

    protected winAnimatorTimerStatus: string;
    protected winAnimatorTimer: FrameTimerEventEmitter;

    protected multipliers: Array<number>;
    protected currentMultiplierIndex: number;

    protected animationNames: Array<string>;
    protected animations: Array<TimelineElement>;
    protected currentAnimationIndex: number;

    protected currentWins: Array<Communication.Game.IRouletteWin>;
    protected currentWin: number;
    protected currentNumber: number;

    protected currentWinGridElements: Array<GridElement>;
    protected currentWinAudio: Array<string>;
    protected currentWinAudioDuration: number;

    private racetrackControls: Element;

    //******************************************************************************************************************
    //  CREATE, INITIALIZE
    //******************************************************************************************************************

    public createByNode(node: Element): void {
        super.createByNode(node);

        if (node.hasAttribute(WinAnimatorAttributes.CONFIGS)) {
            let configs: Array<string> = node.getAttribute(WinAnimatorAttributes.CONFIGS).split(";");

            for (let i = 0; i < configs.length; i++) {
                this.configure(Configuration.Configurations[configs[i]]);
            }
        }
    }

    protected configure(node: Element): void {
        if (node.hasAttribute(WinAnimatorAttributes.WINS_MULTIPLIERS)) {
            this.multipliers = ArrayUtils.numberArray(node.getAttribute(WinAnimatorAttributes.WINS_MULTIPLIERS));
        }

        if (node.hasAttribute(WinAnimatorAttributes.WINS_ANIMATIONS)) {
            this.animationNames = node.getAttribute(WinAnimatorAttributes.WINS_ANIMATIONS).split(";");
        }
    }

    public initialize(
        gridElements: { [id: string]: GridElement },
        betElements: { [id: string]: BetElement },
        gridWinPoint: BaseElement,
        dolly: Dolly,
    ): void {
        this.gridElements = gridElements;
        this.betElements = betElements;
        this.gridWinPoint = gridWinPoint;
        this.dolly = dolly;
        this.racetrackControls = document.getElementById("racetrackControls");

        this.createTimer();
        this.createAnimations();
    }

    //******************************************************************************************************************
    //  SHOW WIN NUMBER
    //******************************************************************************************************************

    public showWinNumber(wins: Array<Communication.Game.IRouletteWin>, win: number, currentNumber: number): void {
        this.currentWins = wins;
        this.currentWin = win;
        this.currentNumber = currentNumber;
        this.currentMultiplierIndex = this.getMultiplierIndex();

        if (this.currentNumber > -1) {
            this.updateWinGridElementsByNumber(this.currentNumber);
            this.playWinNumberAudio(this.currentWinAudio);

            //move dolly to win number
            if (this.dolly != null) {
                const winGridElement = this.gridElements["N_" + this.currentNumber];

                if (winGridElement != null) {
                    let winPosition = {
                        x: winGridElement.position.x,
                        y: winGridElement.position.y + winGridElement.height / 2 - this.dolly.height / 2,
                    };
                    this.dolly.moveToPosition(winPosition as PIXI.Point);
                }
            }
        }

        let showWinNumberDuration = this.currentWinAudioDuration * StageController.FPS;
        showWinNumberDuration = showWinNumberDuration < 150 ? 150 : showWinNumberDuration;

        this.startTimer(WinAnimator.StatusShowWinNumberFinished, showWinNumberDuration);
    }

    private showWinNumberFinished(): void {
        this.emit(WinAnimator.FinishWinNumberAnimator);
    }

    //******************************************************************************************************************
    //  SHOW WINS
    //******************************************************************************************************************

    public showWins(): void {
        //15f wait till layout animation FAST_GRID finished
        this.startTimer(WinAnimator.StatusShowWinCombinationDelay, WinAnimator.ShowWinCombinationDelayDuration);
    }

    private showWinsFinished(): void {
        this.reset();
        this.emit(WinAnimator.FinishWinAnimator);
    }

    private showWinCombinations(): void {
        PlayList.play("SND_BaseBlink");

        if (this.currentWinGridElements.length == 0) {
            this.showWinCombinationsFinished();
            return;
        }

        for (let i = 0; i < this.currentWinGridElements.length; i++) {
            this.currentWinGridElements[i].goToCase(GridElement.CaseWin);
        }

        this.startTimer(WinAnimator.StatusShowWinCombinationFinished, WinAnimator.ShowWinCombinationDuration);
    }

    private showWinCombinationsFinished(): void {
        this.allGridElementsToCase(GridElement.CaseNormal);

        for (let i = 0; i < this.currentWinGridElements.length; i++) {
            this.currentWinGridElements[i].goToCase(GridElement.CaseWinOut);
        }

        this.showWinAnimation();
    }

    private showWinAnimation(): void {
        this.allBetElementsToCase(BetElement.CaseNoWin, true);

        if (this.currentWins != null && this.currentWins.length > 0) {
            let betWinElements = this.getBetElementsByWins(this.currentWins);

            for (let i = 0; i < betWinElements.length; i++) {
                this.updateBetElementWinPoint(betWinElements[i]);
                betWinElements[i].goToCase(BetElement.CaseWin);
            }

            // this.startTimer(WinAnimator.StatusShowWinAnimationFinished, 120);
            this.startAnimation();
        } else {
            this.startTimer(WinAnimator.StatusShowWinsFinished, 30);
        }
    }

    private showWinAnimationFinished(): void {
        this.startAnimation();
    }

    //******************************************************************************************************************
    //  RESET
    //******************************************************************************************************************

    private reset(): void {}

    //******************************************************************************************************************
    //  TIMER
    //******************************************************************************************************************

    protected createTimer(): void {
        this.winAnimatorTimerStatus = "";

        this.winAnimatorTimer = CreateFrameTimer("timer");
        this.winAnimatorTimer.on(FrameTimerEvents.Complete, this.onTimerComplete.bind(this));
    }

    protected startTimer(status: string, duration: number): void {
        this.stopTimer();

        this.winAnimatorTimerStatus = status;
        this.winAnimatorTimer.start(duration);
    }

    protected stopTimer(): void {
        if (this.winAnimatorTimer != null) {
            this.winAnimatorTimer.stop();
        }
    }

    protected onTimerComplete(): void {
        this.winAnimatorTimer.stop();

        switch (this.winAnimatorTimerStatus) {
            case WinAnimator.StatusShowWinNumberFinished:
                this.showWinNumberFinished();
                break;
            case WinAnimator.StatusShowWinCombinationDelay:
                this.showWinCombinations();
                break;
            case WinAnimator.StatusShowWinCombinationFinished:
                this.showWinCombinationsFinished();
                break;
            case WinAnimator.StatusShowWinAnimationFinished:
                this.showWinAnimationFinished();
                break;
            case WinAnimator.StatusShowWinsFinished:
                this.showWinsFinished();
                break;
        }
    }

    //******************************************************************************************************************
    //  ANIMATIONS
    //******************************************************************************************************************

    protected createAnimations(): void {
        this.animations = [];

        for (let i = 0; i < this.animationNames.length; i++) {
            let animation = <TimelineElement>this.childs[this.animationNames[i]];

            if (animation) {
                animation.visible = false;
                animation.on("finished", this.onAnimationFinished.bind(this));
                animation.on(TimelineElement.AnimationComplete, this.onAnimationComplete.bind(this));

                this.animations.push(animation);
            }
        }
    }

    protected startAnimation(): void {
        this.currentAnimationIndex = this.getAnimationIndex();
        this.racetrackControls.classList.add("_disabled");
        const animation = this.animations[this.currentAnimationIndex];
        animation.visible = true;

        if (AppContext.gameConfiguration.UseCoins && BetManager.CoinSize != null) {
            ImageTextCounterElement.Round = true;
            animation.play(parseInt(NumberUtils.devide(this.currentWin, BetManager.CoinSize).toString()));
        } else {
            ImageTextCounterElement.Round = false;
            animation.play(this.currentWin);
        }
    }

    protected getAnimationIndex(): number {
        return this.currentMultiplierIndex;
    }

    protected onAnimationFinished(): void {
        this.emit(WinAnimator.FinishWinCalculation);
    }

    protected onAnimationComplete(): void {
        this.racetrackControls.classList.remove("_disabled");
        this.showWinsFinished();
    }

    //******************************************************************************************************************
    //  GRID ELEMENTS
    //******************************************************************************************************************

    private allGridElementsToCase(caseType: string): void {
        if (this.gridElements != null) {
            for (let key in this.gridElements) {
                this.gridElements[key].goToCase(caseType);
            }
        }
    }

    private updateWinGridElementsByNumber(number: number): void {
        this.currentWinGridElements = [];
        this.currentWinAudio = [];
        this.currentWinAudioDuration = 0;

        if (this.gridElements != null) {
            //prepare audio
            let index = number;
            this.currentWinAudio.push("SND_NUMBER_" + index + (DataManager.CommentaryGender ? "_F" : "_M"));
            this.currentWinAudioDuration +=
                PlayList.Songs["SND_NUMBER_" + index + (DataManager.CommentaryGender ? "_F" : "_M")].getDuration();
            if (DataManager.CommentarySoundEnabled) {
                if (number != 0) {
                    index = RouletteBetType.RedNumbers.indexOf(number) > -1 ? 0 : 1;
                    this.currentWinAudio.push(
                        "SND_NUMBER_" + (index == 0 ? "RED" : "BLACK") + (DataManager.CommentaryGender ? "_F" : "_M"),
                    );
                    this.currentWinAudioDuration +=
                        PlayList.Songs[
                            "SND_NUMBER_" +
                                (index == 0 ? "RED" : "BLACK") +
                                (DataManager.CommentaryGender ? "_F" : "_M")
                        ].getDuration();

                    index = RouletteBetType.EvenNumbers.indexOf(number) > -1 ? 0 : 1;
                    this.currentWinAudio.push(
                        "SND_NUMBER_" + (index == 0 ? "EVEN" : "ODD") + (DataManager.CommentaryGender ? "_F" : "_M"),
                    );
                    this.currentWinAudioDuration +=
                        PlayList.Songs[
                            "SND_NUMBER_" + (index == 0 ? "EVEN" : "ODD") + (DataManager.CommentaryGender ? "_F" : "_M")
                        ].getDuration();
                }
                if (this.currentWins != null && this.currentWins.length > 0) {
                    this.currentWinAudio.push("SND_YouWin_" + (DataManager.CommentaryGender ? "F" : "M"));
                    this.currentWinAudioDuration +=
                        PlayList.Songs["SND_YouWin_" + (DataManager.CommentaryGender ? "F" : "M")].getDuration();
                }
            }

            //prepare win bets
            if (this.currentWins != null) {
                for (let i = 0; i < this.currentWins.length; i++) {
                    let win = this.currentWins[i];
                    let type = this.getGridElementType(win.type);
                    if (
                        ["street", "splitVertical", "doubleStreet", "corner", "trio", "splitHorizontal"].indexOf(
                            win.type,
                        ) != -1
                    ) {
                        this.currentWinGridElements.push(this.gridElements["N_" + this.currentNumber]);
                    }
                    if (this.gridElements[type + "_" + win.selection] != null) {
                        this.currentWinGridElements.push(this.gridElements[type + "_" + win.selection]);
                    }
                }
            }
        }
    }

    private getGridElementType(type: string): string {
        switch (type.toLowerCase()) {
            case RouletteBetType.Single.toLowerCase():
                return "N";
            case RouletteBetType.Dozen.toLowerCase():
                return "D";
            case RouletteBetType.Column.toLowerCase():
                return "C";
            default:
                return type.charAt(0).toUpperCase() + type.slice(1);
        }
    }

    //******************************************************************************************************************
    //  BET ELEMENTS
    //******************************************************************************************************************

    private allBetElementsToCase(caseType: string, excludeEmpty: boolean = false): void {
        if (this.betElements != null) {
            for (let key in this.betElements) {
                let exclude = excludeEmpty && this.betElements[key].getCurrentCase() == BetElement.CaseEmpty;

                if (!exclude) {
                    this.betElements[key].goToCase(caseType);
                }
            }
        }
    }

    private getBetElementsByWins(wins: Array<Communication.Game.IRouletteWin>): Array<BetElement> {
        let elements: Array<BetElement> = [];

        if (this.betElements != null) {
            for (let i = 0; i < wins.length; i++) {
                let win = wins[i];
                let type = win.type.charAt(0).toUpperCase() + win.type.slice(1); //capitalize

                if (this.betElements[type + "_" + win.selection] != null) {
                    elements.push(this.betElements[type + "_" + win.selection]);
                }
            }
        }

        return elements;
    }

    private updateBetElementWinPoint(betElement: BetElement): void {
        if (betElement != null && this.gridWinPoint != null) {
            let x = this.gridWinPoint.x - betElement.x;
            let y = this.gridWinPoint.y - betElement.y;

            betElement.setWinPoint(x, y);
        }
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    private getMultiplierIndex(): number {
        let index = 0;

        for (let i = 0; i < this.multipliers.length; i++) {
            if (this.currentWin / BetManager.Bet >= this.multipliers[i]) {
                index = i;
            }
        }

        return index;
    }

    private playWinNumberAudio(songList: Array<string>): void {
        if (songList && songList.length > 0) {
            let song = PlayList.Songs[songList.shift()];
            let self = this;

            if (song != null) {
                song.setCallback(() => {
                    song.setCallback(null);
                    self.playWinNumberAudio(songList);
                });

                song.play(false, 0, true);
            }
        }
    }
}

export const WinAnimatorAttributes = {
    /**
     * Name of config for win animator.
     * @property Configs
     * @type string
     * @default null
     */
    CONFIGS: "Configs",
    /**
     * Wins multipliers (need WinsAnimations with same array length).
     * @property WinsMultipliers
     * @default null
     */
    WINS_MULTIPLIERS: "WinsMultipliers",
    /**
     * Wins animations (need WinsMultipliers with same array length).
     * @property WinsAnimations
     * @default null
     */
    WINS_ANIMATIONS: "WinsAnimations",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/WinAnimator.ts
 **/